import { CircleCheck, Trash2, CircleAlert, CircleEllipsis, UserX } from 'lucide-react';

import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
} from '@mui/material';

import { SelectedUserStatusesEnum } from '../constants';
import { SelectedUser } from '../types';
import { getUserContact } from '../utils';

import { getStatusColor, getStatusLabel } from './utils';

interface SelectedUsersListProps {
  users: SelectedUser[];
  onRemoveUser: (userId: string) => void;
  isDistributing: boolean;
}

export const SelectedUsersList: React.FC<SelectedUsersListProps> = ({ users, onRemoveUser, isDistributing }) => {
  const getStatusIcon = (status: SelectedUserStatusesEnum) => {
    switch (status) {
      case SelectedUserStatusesEnum.SUCCESS:
        return <CircleCheck color="success" />;
      case SelectedUserStatusesEnum.ERROR:
        return <CircleAlert color="error" />;
      case SelectedUserStatusesEnum.NOT_FOUND:
        return <UserX color="warning" />;
      case SelectedUserStatusesEnum.PENDING:
      default:
        return <CircleEllipsis color="action" />;
    }
  };

  const renderContent = () => {
    if (users.length === 0) {
      return <Typography color="textSecondary">No users selected</Typography>;
    }

    return (
      <List sx={{ maxHeight: 300, overflow: 'auto' }}>
        {users.map((user) => {
          const contact = getUserContact(user);

          return (
            <ListItem key={user.id}>
              <Box sx={{ mr: 1 }}>{getStatusIcon(user.status)}</Box>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>{user.username}</span>
                    {contact !== user.username && (
                      <Typography variant="caption" color="text.secondary">
                        ({contact})
                      </Typography>
                    )}
                  </Box>
                }
              />
              <Chip
                label={getStatusLabel(user.status)}
                color={getStatusColor(user.status)}
                size="small"
                sx={{ mr: 1 }}
              />
              <ListItemSecondaryAction>
                <IconButton edge="end" onClick={() => onRemoveUser(user.id)} disabled={isDistributing}>
                  <Trash2 />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          );
        })}
      </List>
    );
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Selected Users ({users.length})
      </Typography>

      {renderContent()}
    </Box>
  );
};
